import type { IAnyType, IMaybeType, INodeModelType, Instance, ISimpleType, SnapshotOrInstance } from "@gadgetinc/mobx-quick-tree";
import { cast, flow, isAlive, types } from "@gadgetinc/mobx-quick-tree";
import { assert } from "./utils";

let counter = 0;
const debug = false;
const RejectReasontype = types.maybe(types.frozen<unknown>());

/** Thrown when a promise is destroyed sometime after it starts resolving but before it resolves or rejects. */
export class DestroyedPromiseError extends Error {}

type IPromiseModel<Model extends IAnyType, Args extends any[]> = INodeModelType<
  {
    _resolution: IMaybeType<Model>;
    _rejectReason: IMaybeType<ISimpleType<unknown>>;
    args: ISimpleType<Args>;
  },
  {
    current: Instance<IMaybeType<Model>>;
    loading: boolean;
    resolved: boolean;
    rejected: boolean;

    __privateSetResolution: (resolution: SnapshotOrInstance<Model>) => void;
    resolve: () => Promise<Instance<Model>>;
    currentOrSuspend: () => Instance<Model>;
  }
>;

export interface PromiseModelType<Model extends IAnyType, Args extends any[]> extends IPromiseModel<Model, Args> {
  resolved: (value: SnapshotOrInstance<Model>) => Instance<IPromiseModel<Model, Args>>;
}

/**
 * `mobx-state-tree` promise model factory that is observable and plays nice with the atomic middleware. Useful for embedding in the tree to fetch data on demand and reporting it's availability. It's kind of like an Apollo graphql query, but purely just state, more general, and observable.
 * __Note__: This is different than normal `Promise`s because this is a factory: it produces one class of promise (a mst model) where instances of the model get configured with arguments to then execute that specific instance
 */
export const PromiseModel = <T extends IAnyType, Args extends any[]>(
  resolvedType: T,
  resolve: (...args: Args) => Promise<SnapshotOrInstance<T>>,
  resolvedTypeName?: string
): PromiseModelType<T, Args> => {
  const typename = `PromiseModel<${resolvedTypeName || resolvedType.name} - ${counter++}>`;
  const type = types
    .model(typename, {
      _resolution: types.maybe(resolvedType),
      _rejectReason: RejectReasontype,
      args: types.frozen<Args>(),
    })
    .volatile((self) => {
      return {
        loading: false,
        resolved: !!self._resolution,
        rejected: !!self._rejectReason,
      };
    })
    .extend((self) => {
      // this PromiseModel instance should execute exactly once with all the same semantics as a normal, real promise; so it's easiest to implement by wrapping a closed over, real promise.
      let mutexPromise: null | Promise<any> = null;

      return {
        actions: {
          // required because you can't set volatile data from .create calls, so special constructors use this instead
          __privateSetVolatile(data: any) {
            Object.assign(self, data);
          },
          // private method for resetting the state of this promise from authoritative places that know better
          __privateSetResolution(resolution: SnapshotOrInstance<T>) {
            self.loading = false;
            self.resolved = true;
            self.rejected = false;
            self._resolution = resolution;
            self._rejectReason = undefined;
          },
          // this has to be modelled as an action that completes in order for it to commit
          // if it raises, it gets rolled back by the atomic middleware, so we need to not raise, but return something to a view that does raise.
          _runResolve: flow(function* () {
            try {
              const result = yield resolve(...self.args);
              if (!isAlive(self)) return;
              self._resolution = cast(result);
              self.resolved = true;
            } catch (error) {
              if (!isAlive(self)) return;
              self._rejectReason = error;
              self.rejected = true;
            }
            self.loading = false;
            return;
          }),
          // There's an inner wrapper and outer shell for this function so that the mutex is around the whole logic for the resolution, which is important to make sure that the rest of the action above runs before anything can react to the outer promise resolving or rejecting. Those outer reactions require the inner side effects in the above action to have run.
          runResolve: flow(function* () {
            if (!mutexPromise) {
              mutexPromise = (self as any)._runResolve();
            }
            yield assert(mutexPromise);
            return;
          }),
        },
      };
    })
    .views((self) => ({
      get editableInProduction() {
        return true;
      },
      async resolve() {
        if (self.rejected) {
          throw self._rejectReason;
        } else if (self.resolved) {
          return self._resolution as Instance<T>;
        } else {
          await self.runResolve();
          if (!isAlive(self))
            throw new DestroyedPromiseError("PromiseModel was destroyed during resolution, it cannot be resolved or rejected");
          if (self.rejected) {
            throw self._rejectReason;
          } else if (self.resolved) {
            return self._resolution as Instance<T>;
          } else {
            throw new Error("internal PromiseModel error: wasn't resolved or rejected after running resolve");
          }
        }
      },
    }))
    .views((self) => ({
      get current() {
        return self._resolution;
      },
      currentOrSuspend(): Instance<T> {
        if (self.rejected) {
          throw self._rejectReason;
        } else if (self.resolved) {
          return self._resolution as Instance<T>;
        } else {
          // eslint-disable-next-line @typescript-eslint/only-throw-error
          throw new Promise((resolve, reject) => {
            if (debug)
              console.debug(`[promisemodel] promise suspending for ${typename}`, (self as any).$treenode.nodeId, (self as any).toJSON());
            setTimeout(() => {
              self.resolve().then(resolve).catch(reject);
            }, 0);
          });
        }
      },
    }));

  return Object.assign(type, {
    // useful to create an already fulfilled version of this promise that won't ever run the resolver
    resolved(value: SnapshotOrInstance<T>) {
      const instance = type.create({ _resolution: value, args: [] } as any);
      instance.__privateSetVolatile({ resolved: true });
      return instance;
    },
  });
};
